import supabase from './supabase';
import {
  Notification,
  NotificationRow,
  CreateNotificationData,
  NotificationType,
  transformNotificationRow,
} from '../utils/notificationTypes';
import { StaffPositionType } from '../utils/positionTypes';

const NOTIFICATIONS_API_URL = '/api/notifications';

async function getAuthHeader(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ? `Bearer ${session.access_token}` : '';
}

export async function createNotification(
  data: CreateNotificationData,
  sendEmail = true
): Promise<string> {
  const authHeader = await getAuthHeader();
  const response = await fetch(NOTIFICATIONS_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': authHeader,
    },
    body: JSON.stringify({
      action: 'create',
      ...data,
      sendEmail,
    }),
  });

  const result = await response.json();
  if (!response.ok) {
    throw new Error(result.error || 'Failed to create notification');
  }

  return result.id;
}

export async function broadcastNotification(
  positionType: StaffPositionType,
  data: Omit<CreateNotificationData, 'recipientId'>,
  sendEmail = true
): Promise<string[]> {
  const authHeader = await getAuthHeader();
  const response = await fetch(NOTIFICATIONS_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': authHeader,
    },
    body: JSON.stringify({
      action: 'broadcast',
      positionType,
      ...data,
      sendEmail,
    }),
  });

  const result = await response.json();
  if (!response.ok) {
    throw new Error(result.error || 'Failed to broadcast notification');
  }

  return result.ids;
}

export async function getNotifications(
  userId: string,
  options: { limit?: number; offset?: number; unreadOnly?: boolean } = {}
): Promise<{ notifications: Notification[]; totalCount: number }> {
  const { limit = 20, offset = 0, unreadOnly = false } = options;

  let query = supabase
    .from('notifications')
    .select('*', { count: 'exact' })
    .eq('recipient_id', userId)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (unreadOnly) {
    query = query.eq('is_read', false);
  }

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching notifications:', error);
    throw new Error('Failed to fetch notifications');
  }

  return {
    notifications: (data || []).map((row) => transformNotificationRow(row as NotificationRow)),
    totalCount: count || 0,
  };
}

export async function getUnreadCount(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('recipient_id', userId)
    .eq('is_read', false);

  if (error) {
    console.error('Error fetching unread count:', error);
    return 0;
  }

  return count || 0;
}

export async function markAsRead(notificationId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({
      is_read: true,
      read_at: new Date().toISOString(),
    })
    .eq('id', notificationId)
    .eq('recipient_id', userId);

  if (error) {
    console.error('Error marking notification as read:', error);
    throw new Error('Failed to mark notification as read');
  }
}

export async function markAllAsRead(userId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({
      is_read: true,
      read_at: new Date().toISOString(),
    })
    .eq('recipient_id', userId)
    .eq('is_read', false);

  if (error) {
    console.error('Error marking all notifications as read:', error);
    throw new Error('Failed to mark all notifications as read');
  }
}

/** Opening Flagged Notes acknowledges every unread case_note_flagged notification. */
export async function markCaseNoteFlagsRead(userId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq('recipient_id', userId)
    .eq('type', 'case_note_flagged')
    .eq('is_read', false);
  if (error) throw error;
}

export async function getNotificationsByType(
  userId: string,
  type: NotificationType,
  options: { limit?: number; unreadOnly?: boolean } = {}
): Promise<Notification[]> {
  const { limit = 10, unreadOnly = false } = options;

  let query = supabase
    .from('notifications')
    .select('*')
    .eq('recipient_id', userId)
    .eq('type', type)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (unreadOnly) {
    query = query.eq('is_read', false);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching notifications by type:', error);
    return [];
  }

  return (data || []).map((row) => transformNotificationRow(row as NotificationRow));
}
